import { Mark, mergeAttributes } from '@tiptap/core'

/**
 * The text highlight ("surlignage", AB#277) of a prose part: a background behind the words, a mark
 * of its own — it is not a text colour and never touches one.
 *
 * WHY A MARKDOWN SYNTAX. A part IS its markdown, and tiptap-markdown runs with `html: false`, so a
 * `<mark>` would be dropped on the next serialisation. The highlight is written in the
 * `markdown-it-mark` form most tools already read, with the colour as a trailing attribute:
 *
 *   ==words==             the default (yellow) highlight
 *   ==words=={green}      a named colour of HIGHLIGHT_COLOURS
 *   ==words=={#FFCC00}    any other colour, as #RRGGBB (AB#287: picked in the common colour panel)
 *
 * The colour goes AFTER the closer because tiptap-markdown re-flows the OPENING delimiter of a
 * mark around whitespace (its `expelEnclosingWhitespace` pass treats `open` as a fixed string).
 *
 * PARITY. `backend/adapters/markdown/highlight-markdown.ts` is the same extension for the server
 * canonicaliser — both must produce the same bytes, which the `highlight-*` cases of
 * `shared/md-canonical.fixture.json` prove. The exports read the same syntax
 * (backend/domain/highlight.ts).
 */

/** The prose highlight palette, by name; `yellow` is the default and is written without `{…}`. */
export const HIGHLIGHT_COLOURS = ['yellow', 'green', 'blue', 'pink', 'orange'] as const
export type HighlightColour = (typeof HIGHLIGHT_COLOURS)[number]

export const DEFAULT_HIGHLIGHT: HighlightColour = 'yellow'

export const isHighlightColour = (v: unknown): v is HighlightColour =>
  typeof v === 'string' && (HIGHLIGHT_COLOURS as readonly string[]).includes(v)

/** A highlight as stored: a named colour, or a custom one as `#RRGGBB` (upper case). */
export type HighlightValue = HighlightColour | `#${string}`

/** `v` as a highlight value (a name, or `#rrggbb` upper-cased), or null when it is neither. */
export function toHighlightValue(v: unknown): HighlightValue | null {
  if (isHighlightColour(v)) return v
  return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? (v.toUpperCase() as HighlightValue) : null
}

/** The DOM attribute the markdown-it rule hands the colour over in. */
const COLOUR_ATTR = 'data-highlight'

/** Minimal structural view of markdown-it — just what the inline rule touches. */
interface MdToken {
  attrs: Array<[string, string]> | null
  markup: string
}
interface MdInlineState {
  src: string
  pos: number
  posMax: number
  md: { inline: { tokenize(state: MdInlineState): void } }
  push(type: string, tag: string, nesting: number): MdToken
}
interface MdIt {
  inline: { ruler: { before(before: string, name: string, rule: (state: MdInlineState, silent: boolean) => boolean): void } }
}

/** tiptap-markdown runs every extension's `parse.setup` before EVERY parse, on one long-lived
 *  markdown-it instance — register the rule once per instance. */
const withRule = new WeakSet<object>()

const isSpace = (c: string | undefined): boolean => c === undefined || /\s/.test(c)

/** Index of the `==` closing a highlight whose content starts at `from`, or -1. Escapes and code
 *  spans are skipped, so `\==` and `` `a==b` `` never close it. */
function findClose(src: string, from: number, max: number): number {
  for (let i = from; i < max - 1; i++) {
    const c = src[i]
    if (c === '\\') {
      i++
      continue
    }
    if (c === '`') {
      let run = 1
      while (src[i + run] === '`') run++
      const fence = '`'.repeat(run)
      const end = src.indexOf(fence, i + run)
      if (end >= 0 && end < max) i = end + run - 1
      else i += run - 1
      continue
    }
    if (c === '=' && src[i + 1] === '=' && i > from && !isSpace(src[i - 1])) return i
  }
  return -1
}

/**
 * The markdown-it inline rule: `==content==` with an optional `{colour}` / `{#RRGGBB}` right after the closer.
 * An unknown `{…}` is not consumed (it stays text and the highlight is the default yellow).
 */
export function highlightRule(state: MdInlineState, silent: boolean): boolean {
  const { src, pos: start, posMax: max } = state
  if (src[start] !== '=' || src[start + 1] !== '=') return false
  const from = start + 2
  if (from >= max || isSpace(src[from]) || src[from] === '=') return false
  const close = findClose(src, from, max)
  if (close < 0) return false
  let end = close + 2
  let colour: HighlightValue = DEFAULT_HIGHLIGHT
  const attr = /^\{([a-z]+|#[0-9a-fA-F]{6})\}/.exec(src.slice(end, max))
  const v = attr ? toHighlightValue(attr[1]) : null
  if (attr && v) {
    colour = v
    end += attr[0].length
  }
  if (!silent) {
    const open = state.push('niobe_highlight_open', 'mark', 1)
    open.attrs = [[COLOUR_ATTR, colour]]
    open.markup = '=='
    state.pos = from
    state.posMax = close
    state.md.inline.tokenize(state)
    state.posMax = max
    state.push('niobe_highlight_close', 'mark', -1).markup = '=='
  }
  state.pos = end
  return true
}

/** The markdown of a highlight mark: `==` … `==` plus `{colour}` unless it is the default. */
export function highlightClose(colour: unknown): string {
  const v = toHighlightValue(colour)
  return v && v !== DEFAULT_HIGHLIGHT ? `=={${v}}` : '=='
}

export const ProseHighlight = Mark.create({
  name: 'highlight',
  // Ranked BEFORE bold / italic / strike / code (priority 100), so it is the OUTER mark: a highlight
  // over `**bold** and *em*` is written as one `==…==` span rather than closed and reopened around
  // each inner mark (a reopened `== and` would no longer parse as a highlight).
  priority: 150,

  addAttributes() {
    return {
      color: {
        default: DEFAULT_HIGHLIGHT,
        // A pasted `<mark>` with no (or a foreign) colour is the default highlighter.
        parseHTML: (el: HTMLElement) => toHighlightValue(el.getAttribute(COLOUR_ATTR)) ?? DEFAULT_HIGHLIGHT,
        // A named colour is drawn by its --hl-* token (styles.css); a custom one inline.
        renderHTML: (attrs: { color?: unknown }) => {
          const v = toHighlightValue(attrs.color) ?? DEFAULT_HIGHLIGHT
          return v.startsWith('#') ? { [COLOUR_ATTR]: v, style: `background:${v}` } : { [COLOUR_ATTR]: v }
        },
      },
    }
  },

  parseHTML() {
    return [{ tag: 'mark' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(HTMLAttributes), 0]
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '==',
          close: (_state: unknown, mark: { attrs: { color?: unknown } }) => highlightClose(mark.attrs.color),
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(markdownit: MdIt) {
            if (withRule.has(markdownit)) return
            withRule.add(markdownit)
            markdownit.inline.ruler.before('emphasis', 'niobe_highlight', highlightRule)
          },
        },
      },
    }
  },
})
